import { useState, useRef, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getConversations, streamChat, testChat } from '../lib/api';
import { supabase } from '../lib/supabase';
import { useNavigate } from 'react-router-dom';

/* ── WhatsApp exact colors (Web spec) ── */
const WA = {
  bg: '#efeae2',
  headerBg: '#ffffff',
  headerText: '#111B21',
  headerBorder: '#e0e0e0',
  outgoing: '#dcf8c6',
  incoming: '#ffffff',
  text: '#111',
  meta: '#667781',
  tickBlue: '#53BDEB',
  tickGrey: '#B3B8BD',
  inputBarBg: '#f0f2f5',
  inputBg: '#ffffff',
  sendBtn: '#008069',
  scrollBtn: '#ffffff',
};

/* ── Rich WhatsApp-style doodle background: 400×400, 40+ outline icons, varied sizes ── */
function buildDoodleSvg() {
  const s = '#9c9285';
  const A = `fill="none" stroke="${s}" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"`;
  const FI = `fill="${s}"`;

  const heart  = () => `<path ${A} d="M0,-3 C0,-8 -9,-8 -9,-3 C-9,2 0,9 0,9 C0,9 9,2 9,-3 C9,-8 0,-8 0,-3z"/>`;
  const star   = () => `<polygon ${A} points="0,-10 2.94,-4.05 9.51,-3.09 4.75,1.55 5.88,8.09 0,5 -5.88,8.09 -4.75,1.55 -9.51,-3.09 -2.94,-4.05"/>`;
  const bubble = () => `<path ${A} d="M-9,-8 Q-9,-10 -7,-10 L7,-10 Q9,-10 9,-8 L9,3 Q9,5 7,5 L1,5 L-1,9 L-1,5 L-7,5 Q-9,5 -9,3z"/>`;
  const smiley = () => `<circle ${A} cx="0" cy="0" r="10"/><circle ${FI} cx="-3.5" cy="-2" r="1.5"/><circle ${FI} cx="3.5" cy="-2" r="1.5"/><path ${A} d="M-4,3 Q0,7 4,3"/>`;
  const bolt   = () => `<path ${A} d="M3,-10 L-4,0 L1,0 L-3,10 L8,-1 L2,-1z"/>`;
  const fire   = () => `<path ${A} d="M0,9 C-7,9 -8,2 -4,-1 C-2,-3 -3,-7 0,-9 C1,-5 3,-4 2,-1 C4,-5 5,-7 3,-9 C7,-6 8,2 5,5 C3,7 2,9 0,9z"/>`;
  const note   = () => `<path ${A} d="M-2,7 Q-6,8 -6,5 Q-6,2 -2,2 Q2,2 2,5 L2,-5 L8,-7 L8,0 Q4,1 4,-2"/>`;
  const env    = () => `<path ${A} d="M-9,-7 L-9,8 Q-9,9 -8,9 L8,9 Q9,9 9,8 L9,-7 Q9,-8 8,-8 L-8,-8 Q-9,-8 -9,-7z M-9,-7 L0,1 L9,-7"/>`;
  const gem    = () => `<polygon ${A} points="0,-10 7,0 0,10 -7,0"/>`;
  const leaf   = () => `<path ${A} d="M0,9 C-8,7 -10,-1 0,-9 C10,-1 8,7 0,9z M0,-9 L0,9"/>`;
  const cloud  = () => `<path ${A} d="M-5,4 Q-9,4 -9,-1 Q-9,-5 -5,-6 Q-4,-10 -1,-10 Q2,-12 5,-10 Q8,-11 10,-8 Q12,-6 9,-3 Q9,4 6,4 Q1,5 -5,4z"/>`;
  const moon   = () => `<path ${A} d="M-2,-9 Q6,-9 8,-2 Q6,8 -2,9 Q3,4 3,-1 Q3,-7 -2,-9z"/>`;
  const clock  = () => `<circle ${A} cx="0" cy="0" r="10"/><path ${A} d="M0,0 L0,-6 M0,0 L5,4"/>`;
  const house  = () => `<path ${A} d="M0,-10 L-9,-1 L-7,-1 L-7,9 L7,9 L7,-1 L9,-1z M-3,9 L-3,3 L3,3 L3,9"/>`;
  const phone  = () => `<rect ${A} x="-5" y="-9" width="10" height="18" rx="2"/><circle ${FI} cx="0" cy="6" r="1.5"/>`;
  const cam    = () => `<path ${A} d="M-9,-5 Q-10,-5 -10,-4 L-10,7 Q-10,8 -9,8 L9,8 Q10,8 10,7 L10,-4 Q10,-5 9,-5 L6,-5 L5,-8 L-5,-8 L-6,-5z"/><circle ${A} cx="0" cy="1" r="4"/>`;
  const cup    = () => `<path ${A} d="M-7,-7 L-5,9 Q-5,10 -4,10 L4,10 Q5,10 5,9 L7,-7z M7,-4 Q10,-4 10,0 Q10,4 7,4"/>`;
  const pencil = () => `<path ${A} d="M-2,-9 L2,-9 L2,7 L0,10 L-2,7z M-2,-9 L-2,-12 Q0,-13 2,-12 L2,-9"/>`;
  const ballon = () => `<ellipse ${A} cx="0" cy="-2" rx="7" ry="9"/><path ${A} d="M0,7 Q2,10 4,12"/>`;
  const arrow  = () => `<path ${A} d="M-8,0 L5,0 M1,-5 L8,0 L1,5"/>`;
  const key    = () => `<circle ${A} cx="-4" cy="0" r="5"/><path ${A} d="M1,0 L9,0 M9,-2 L9,2 M7,-2 L7,2"/>`;
  const sun    = () => { let r=`<circle ${A} cx="0" cy="0" r="5"/>`;for(let i=0;i<8;i++){const ag=i*45*Math.PI/180;r+=`<line ${A} x1="${(Math.cos(ag)*7).toFixed(1)}" y1="${(Math.sin(ag)*7).toFixed(1)}" x2="${(Math.cos(ag)*10).toFixed(1)}" y2="${(Math.sin(ag)*10).toFixed(1)}"/>`;}return r;};
  const flower = () => { let p=`<circle ${A} cx="0" cy="0" r="3"/>`;for(let i=0;i<6;i++)p+=`<ellipse ${A} cx="0" cy="-8" rx="2.5" ry="4.5" transform="rotate(${i*60})"/>`;return p;};

  /* [iconFn, cx, cy, scale, opacity] — 6 rows × 7 cols + extras = 44 icons */
  const P = [
    [heart, 35,28,.9,.33],[star,  88,20,.8,.30],[bubble,155,32,.9,.30],[smiley,228,22,.8,.28],[bolt,  285,38,.8,.32],[flower,348,22,.85,.30],[phone, 392,40,.7,.28],
    [fire,  28,98,.9,.30],[note,  88,92,.85,.32],[env,   155,100,.8,.30],[gem,  218,92,.85,.28],[cloud, 285,98,.8,.30],[pencil,355,95,.85,.32],[arrow,  392,98,.8,.28],
    [heart, 20,168,.7,.30],[clock, 75,162,.85,.30],[house, 142,170,.8,.30],[leaf, 210,158,.85,.32],[cam,  278,165,.8,.30],[cup,   348,168,.85,.28],[moon,  392,157,.75,.30],
    [sun,   45,238,.9,.30],[star,  108,230,.7,.32],[ballon,172,235,.85,.28],[bubble,240,228,.75,.30],[fire, 305,240,.8,.30],[env,   372,228,.75,.32],[key,   392,240,.8,.28],
    [flower,30,308,.8,.30],[bolt,  88,298,.75,.30],[smiley,152,310,.8,.28],[heart, 222,300,.95,.33],[clock,288,305,.8,.30],[pencil,358,298,.8,.32],[gem,   392,308,.75,.28],
    [moon,  50,372,.85,.28],[note, 115,368,.8,.30],[leaf,  180,378,.75,.30],[house, 248,365,.8,.32],[cam,  318,372,.8,.28],[sun,   380,362,.85,.30],[ballon,392,372,.75,.28],
  ];

  return `<svg xmlns="http://www.w3.org/2000/svg" width="400" height="400">${P.map(([f,x,y,sc,op])=>`<g transform="translate(${x},${y}) scale(${sc})" opacity="${op}">${f()}</g>`).join('')}</svg>`;
}
const _dsvg = buildDoodleSvg();
const doodleBg = `url("data:image/svg+xml,${encodeURIComponent(_dsvg)}")`;

const DoubleTick = ({ read }) => (
  <svg width="16" height="11" viewBox="0 0 16 11" className="inline-block ml-1 -mb-px shrink-0">
    <path d="M11.07.66L5.69 7.7 3.17 5.28a.72.72 0 00-1.02.98l3.04 2.96a.72.72 0 001.04-.04l5.84-7.56a.72.72 0 00-1.08-.96z"
      fill={read ? WA.tickBlue : WA.tickGrey}/>
    <path d="M14.07.66L8.69 7.7 7.44 6.5l-.98 1.24 1.74 1.7a.72.72 0 001.04-.04l5.84-7.56a.72.72 0 00-1.08-.96z"
      fill={read ? WA.tickBlue : WA.tickGrey}/>
  </svg>
);

const TypingDots = () => (
  <div className="flex items-center gap-[3px] py-2 px-1">
    {[0, 1, 2].map(i => (
      <span key={i} className="block w-[7px] h-[7px] rounded-full"
        style={{
          background: '#9CA3AF',
          animation: `waTyping 1.4s infinite ease-in-out`,
          animationDelay: `${i * 0.2}s`
        }}/>
    ))}
    <style>{`
      @keyframes waTyping {
        0%, 60%, 100% { transform: translateY(0); opacity: .4 }
        30% { transform: translateY(-4px); opacity: 1 }
      }
    `}</style>
  </div>
);

/* Small source-channel badge shown below message timestamp */
const ChannelBadge = ({ source }) => {
  if (!source) return null;

  if (source === 'whatsapp') return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 10, color: '#25D366', opacity: 0.85 }}>
      <svg width="10" height="10" viewBox="0 0 24 24" fill="#25D366">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
      </svg>
      WhatsApp
    </span>
  );

  if (source === 'telegram') return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 10, color: '#2AABEE', opacity: 0.85 }}>
      <svg width="10" height="10" viewBox="0 0 24 24" fill="#2AABEE">
        <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.894 8.221l-1.97 9.28c-.145.658-.537.818-1.084.508l-3-2.21-1.447 1.394c-.16.16-.295.295-.605.295l.213-3.053 5.56-5.023c.242-.213-.054-.333-.373-.12l-6.871 4.326-2.962-.924c-.643-.204-.657-.643.136-.953l11.57-4.461c.537-.194 1.006.131.833.941z"/>
      </svg>
      Telegram
    </span>
  );

  if (source === 'web') return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 10, color: '#60A5FA', opacity: 0.85 }}>
      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#60A5FA" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/>
        <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
      </svg>
      Web
    </span>
  );

  if (source === 'hook') return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 10, color: '#F59E0B', opacity: 0.85 }}>
      ⚡ auto
    </span>
  );

  return null;
};

const ChatPage = () => {
  const { userProfile, business } = useAuth();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [streamingText, setStreamingText] = useState('');
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const [showScrollDown, setShowScrollDown] = useState(false);
  const [channels, setChannels] = useState({ whatsapp: false, telegram: false });
  const scrollRef = useRef(null);
  const bottomRef = useRef(null);
  const lastCountRef = useRef(0);
  const rtRef = useRef(null);
  const pollRef = useRef(null);
  const inputRef = useRef(null);

  // Determine which channels are active from the user profile
  useEffect(() => {
    if (userProfile) {
      setChannels({
        whatsapp: !!userProfile.phone,
        telegram: !!userProfile.telegram_chat_id,
      });
    }
  }, [userProfile]);

  const setupRealtime = useCallback(() => {
    if (!userProfile?.id) return;
    const ch = supabase
      .channel(`chat-${userProfile.id}`)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'conversations',
        filter: `user_id=eq.${userProfile.id}`
      }, (payload) => {
        // Skip WhatsApp observability rows — only sync web-channel rows to the chat UI
        if (payload.new?.channel === 'whatsapp') return;
        const m = payload.new?.messages;
        if (m && Array.isArray(m)) syncFromServer(m);
      })
      .subscribe((s) => { if (s === 'CHANNEL_ERROR' || s === 'TIMED_OUT') startPoll(); });
    rtRef.current = ch;
    // syncFromServer/startPoll are stable per-render (closures over refs only).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userProfile?.id]);

  const startPoll = useCallback(() => {
    if (pollRef.current) return;
    pollRef.current = setInterval(async () => {
      if (!userProfile?.id) return;
      try {
        const r = await getConversations(userProfile.id);
        const s = r.data || [];
        if (s.length > lastCountRef.current) syncFromServer(s);
      } catch (err) {
        console.warn('[ChatPage] poll error:', err);
      }
    }, 3000);
  }, [userProfile?.id]);

  // Full-replace sync from server — used by Realtime and polling.
  // Deduplicates against in-flight optimistic messages so no double-posts.
  const syncFromServer = (serverMessages) => {
    if (!serverMessages?.length) return;
    setMessages(prev => {
      // Build a set of {role+content} already in the UI (from optimistic/streaming messages
      // added by handleSend that haven't yet been flushed to the server).
      const serverSet = new Set(serverMessages.map(m => `${m.role}|${m.content}`));
      // Optimistic messages: exist in UI but NOT yet in the server snapshot
      const optimistic = prev.filter(m => !serverSet.has(`${m.role}|${m.content}`));

      const formatted = serverMessages.map((m, i) => ({
        id: `srv-${i}`,
        role: m.role === 'user' ? 'user' : 'assistant',
        content: m.content,
        timestamp: m.timestamp || new Date().toISOString(),
        is_voice_note: m.is_voice_note || false,
        voice_transcription: m.voice_transcription || null,
        source: m.source || null,
      }));

      // Merge: server (authoritative) + any in-flight optimistic messages
      const merged = [...formatted, ...optimistic];
      lastCountRef.current = merged.length;
      return merged;
    });
  };

  const scrollToBottom = useCallback((b = 'smooth') => {
    bottomRef.current?.scrollIntoView({ behavior: b });
  }, []);

  useEffect(() => {
    if (messages.length > 0 || streamingText) scrollToBottom(historyLoaded ? 'smooth' : 'instant');
  }, [messages, streamingText, historyLoaded, scrollToBottom]);

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setShowScrollDown(el.scrollHeight - el.scrollTop - el.clientHeight > 120);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (el) { el.addEventListener('scroll', handleScroll); return () => el.removeEventListener('scroll', handleScroll); }
  }, [handleScroll]);

  const loadHistory = useCallback(async () => {
    try {
      const r = await getConversations(userProfile.id);
      const h = r.data || [];
      if (h.length > 0) {
        setMessages(h.map((m, i) => ({
          id: `h-${i}`,
          role: m.role === 'user' ? 'user' : 'assistant',
          content: m.content,
          timestamp: m.timestamp || new Date().toISOString(),
          is_voice_note: m.is_voice_note || false,
          voice_transcription: m.voice_transcription || null,
          source: m.source || null,
        })));
        lastCountRef.current = h.length;
      }
    } catch (e) { console.error('[ChatPage] loadHistory error:', e); }
    finally { setHistoryLoaded(true); }
  }, [userProfile?.id]);

  // Mount-time bootstrap effect — placed AFTER loadHistory + setupRealtime
  // useCallback declarations so we don't read const bindings inside the TDZ.
  useEffect(() => {
    if (userProfile?.id && !historyLoaded) {
      loadHistory();
      setupRealtime();
    }
    return () => {
      if (rtRef.current) supabase.removeChannel(rtRef.current);
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [userProfile?.id, historyLoaded, loadHistory, setupRealtime]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!input.trim() || sending) return;
    const msg = { id: `s-${Date.now()}`, role: 'user', content: input.trim(), timestamp: new Date().toISOString() };
    setMessages(prev => [...prev, msg]);
    const text = input.trim();
    setInput('');
    setSending(true);
    setStreamingText('');
    inputRef.current?.focus();

    try {
      let acc = '';
      await streamChat(userProfile?.phone || '+919876543200', text,
        (chunk) => { acc += chunk; setStreamingText(acc); },
        (full) => {
          setStreamingText('');
          const response = full || acc;
          setMessages(prev => {
            // Dedup: Realtime may have already synced this bot message via save_conversation
            if (prev.some(m => m.role === 'assistant' && m.content === response)) return prev;
            const updated = [...prev, { id: `r-${Date.now()}`, role: 'assistant', content: response, timestamp: new Date().toISOString() }];
            lastCountRef.current = updated.length;
            return updated;
          });
          setSending(false);
        },
        () => {
          setStreamingText('');
          testChat(userProfile?.phone || '+919876543200', text)
            .then(res => {
              const c = res.data.response || res.data.question || 'Something went wrong.';
              setMessages(prev => {
                if (prev.some(m => m.role === 'assistant' && m.content === c)) return prev;
                const updated = [...prev, { id: `r-${Date.now()}`, role: 'assistant', content: c, timestamp: new Date().toISOString() }];
                lastCountRef.current = updated.length;
                return updated;
              });
            })
            .catch(() => { setMessages(prev => [...prev, { id: `e-${Date.now()}`, role: 'assistant', content: 'Sorry, kuch gadbad ho gayi.', timestamp: new Date().toISOString() }]); })
            .finally(() => setSending(false));
        },
        (hookMsg) => {
          // ack / progress hook — show as a distinct bot bubble before streaming starts
          setMessages(prev => {
            if (prev.some(m => m.content === hookMsg && m.hook)) return prev;
            return [...prev, { id: `h-${Date.now()}`, role: 'assistant', content: hookMsg, hook: true, timestamp: new Date().toISOString() }];
          });
        }
      );
    } catch {
      setStreamingText('');
      setSending(false);
      setMessages(prev => [...prev, { id: `e-${Date.now()}`, role: 'assistant', content: 'Sorry, kuch gadbad ho gayi.', timestamp: new Date().toISOString() }]);
    }
  };

  const fmtTime = (t) => new Date(t).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });

  const quickActions = [
    { label: 'Trip kiya', msg: 'Aaj 3 trip cement kiya' },
    { label: 'Stock check', msg: 'Cement ka stock kitna hai?' },
    { label: 'Report', msg: 'Aaj ka report dikhao' },
    { label: 'Payment', msg: 'Ramesh se 5000 payment mila' }
  ];

  return (
    <div className="h-screen flex flex-col relative" style={{ background: WA.bg }} data-testid="chat-page">

      {/* ── Header ── */}
      <header className="shrink-0 flex items-center gap-3 px-4 py-2.5" style={{ background: WA.headerBg, borderBottom: `1px solid ${WA.headerBorder}` }}>
        <button onClick={() => navigate('/dashboard')} className="p-1 -ml-1 rounded-full transition hover:bg-black/5" data-testid="back-button">
          <svg className="w-5 h-5" fill="none" stroke={WA.headerText} strokeWidth={2.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7"/></svg>
        </button>

        <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0" style={{ background: '#dfe5ea' }}>
          <span className="font-bold text-sm" style={{ color: '#54656F' }}>N</span>
        </div>

        <div className="flex-1 min-w-0">
          <h1 className="font-semibold text-[17px] leading-tight truncate" style={{ color: WA.headerText }}>Noteho AI</h1>
          <div className="flex items-center gap-2 mt-[2px]">
            <span className="text-[12px] leading-tight" style={{ color: WA.meta }}>
              {sending ? 'typing...' : 'online'}
            </span>
            {/* Channel presence indicators */}
            <span className="flex items-center gap-1.5">
              {channels.whatsapp && (
                <span
                  data-testid="whatsapp-indicator"
                  title="WhatsApp connected"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 11,
                    background: '#e9fbe9', color: '#128C7E', padding: '1px 6px 1px 4px',
                    borderRadius: 99, border: '1px solid #c5eecc' }}>
                  {/* Pulsing live dot */}
                  <span style={{
                    width: 6, height: 6, borderRadius: '50%', background: '#25D366',
                    display: 'inline-block', flexShrink: 0,
                    animation: 'waPulse 2s ease-in-out infinite'
                  }}/>
                  {/* WhatsApp icon */}
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="#25D366">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                  <span className="font-medium hidden sm:inline">WhatsApp</span>
                </span>
              )}
              {channels.telegram && (
                <span
                  data-testid="telegram-indicator"
                  title="Telegram connected"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 11,
                    background: '#e8f4fd', color: '#1a7bbf', padding: '1px 6px 1px 4px',
                    borderRadius: 99, border: '1px solid #b3d9f5' }}>
                  <span style={{
                    width: 6, height: 6, borderRadius: '50%', background: '#2AABEE',
                    display: 'inline-block', flexShrink: 0,
                    animation: 'waPulse 2s ease-in-out infinite 0.5s'
                  }}/>
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="#2AABEE">
                    <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.894 8.221l-1.97 9.28c-.145.658-.537.818-1.084.508l-3-2.21-1.447 1.394c-.16.16-.295.295-.605.295l.213-3.053 5.56-5.023c.242-.213-.054-.333-.373-.12l-6.871 4.326-2.962-.924c-.643-.204-.657-.643.136-.953l11.57-4.461c.537-.194 1.006.131.833.941z"/>
                  </svg>
                  <span className="font-medium hidden sm:inline">Telegram</span>
                </span>
              )}
              {!channels.telegram && (
                <span
                  data-testid="telegram-link-hint"
                  title="Link Telegram: open @Noteho_bot and tap Start"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 11,
                    background: '#f8f8f8', color: '#aaa', padding: '1px 6px 1px 4px',
                    borderRadius: 99, border: '1px solid #e0e0e0', cursor: 'default' }}>
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="#ccc">
                    <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.894 8.221l-1.97 9.28c-.145.658-.537.818-1.084.508l-3-2.21-1.447 1.394c-.16.16-.295.295-.605.295l.213-3.053 5.56-5.023c.242-.213-.054-.333-.373-.12l-6.871 4.326-2.962-.924c-.643-.204-.657-.643.136-.953l11.57-4.461c.537-.194 1.006.131.833.941z"/>
                  </svg>
                  <span className="hidden sm:inline">Link Telegram</span>
                </span>
              )}
            </span>
          </div>
        </div>

        {/* Pulse animation keyframes */}
        <style>{`
          @keyframes waPulse {
            0%, 100% { opacity: 1; transform: scale(1); }
            50%       { opacity: 0.5; transform: scale(0.75); }
          }
        `}</style>
      </header>

      {/* ── Messages area ── */}
      <div className="flex-1 overflow-y-auto relative" ref={scrollRef}
        style={{ background: WA.bg, backgroundImage: doodleBg, backgroundSize: '400px 400px' }}>

        <div className="max-w-3xl mx-auto px-3 py-2" data-testid="messages-container">

          {messages.length > 0 && (
            <div className="flex justify-center my-3">
              <span className="px-3 py-1 rounded-lg text-[12.5px] shadow-sm" style={{ background: '#E2DBCF', color: '#54656F' }}>Today</span>
            </div>
          )}

          {messages.map((m) => {
            const isUser = m.role === 'user';
            return (
              <div key={m.id} className={`flex mb-[3px] ${isUser ? 'justify-end' : 'justify-start'}`} data-testid={`message-${m.id}`}>
                <div className="relative max-w-[85%] md:max-w-[65%] px-[9px] pt-[6px] pb-[8px] shadow-sm"
                  style={{
                    background: isUser ? WA.outgoing : WA.incoming,
                    borderRadius: isUser ? '7.5px 0 7.5px 7.5px' : '0 7.5px 7.5px 7.5px',
                  }}>

                  {/* Tail */}
                  <svg className={`absolute top-0 ${isUser ? '-right-[8px]' : '-left-[8px]'}`}
                    width="8" height="13" viewBox="0 0 8 13">
                    {isUser
                      ? <path d="M0 0 C0 6 3.4 10.1 7.9 11.4 L8 0 Z" fill={WA.outgoing}/>
                      : <path d="M8 0 C8 6 4.6 10.1 0.1 11.4 L0 0 Z" fill={WA.incoming}/>
                    }
                  </svg>

                  <p className="text-[14.2px] leading-[19px] whitespace-pre-wrap" style={{ color: WA.text }}>
                    {m.is_voice_note && (
                      <span className="text-[11px] mr-1 opacity-70" title={m.voice_transcription ? `Voice: ${m.voice_transcription}` : 'Voice note'}>
                        🎙️
                      </span>
                    )}
                    {m.content}
                  </p>

                  <div className="flex items-center justify-end gap-[3px] mt-[2px] -mb-[2px] select-none">
                    {m.source && <ChannelBadge source={m.source} />}
                    <span className="text-[11px] leading-none" style={{ color: WA.meta }}>{fmtTime(m.timestamp)}</span>
                    {isUser && <DoubleTick read={true}/>}
                  </div>
                </div>
              </div>
            );
          })}

          {/* Streaming / typing */}
          {sending && (
            <div className="flex justify-start mb-[3px]">
              <div className="relative max-w-[85%] md:max-w-[65%] px-[9px] pt-[6px] pb-[8px] shadow-sm"
                style={{ background: WA.incoming, borderRadius: '0 7.5px 7.5px 7.5px' }}>
                <svg className="absolute top-0 -left-[8px]" width="8" height="13" viewBox="0 0 8 13">
                  <path d="M8 0 C8 6 4.6 10.1 0.1 11.4 L0 0 Z" fill={WA.incoming}/>
                </svg>
                {streamingText ? (
                  <p className="text-[14.2px] leading-[19px] whitespace-pre-wrap" style={{ color: WA.text }}>
                    {streamingText}
                    <span className="inline-block w-[2px] h-[16px] ml-[2px] align-text-bottom animate-pulse" style={{ background: WA.sendBtn }}/>
                  </p>
                ) : (
                  <TypingDots/>
                )}
              </div>
            </div>
          )}

          <div ref={bottomRef}/>
        </div>

        {/* Scroll-to-bottom — centered above input */}
        {showScrollDown && (
          <button onClick={() => scrollToBottom('smooth')}
            className="absolute bottom-4 left-1/2 -translate-x-1/2 z-50 w-10 h-10 rounded-full shadow-lg flex items-center justify-center transition-all hover:scale-105"
            style={{ background: WA.scrollBtn }}
            data-testid="scroll-to-bottom-btn">
            <svg className="w-5 h-5" fill={WA.meta} viewBox="0 0 24 24"><path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z"/></svg>
          </button>
        )}
      </div>

      {/* Quick actions */}
      <div className="shrink-0 px-3 pt-1 pb-0.5" style={{ background: WA.inputBarBg }}>
        <div className="flex gap-1.5 overflow-x-auto scrollbar-hide">
          {quickActions.map((a, i) => (
            <button key={i}
              className="whitespace-nowrap px-3 py-1 rounded-full text-[13px] transition-colors border"
              style={{ color: WA.sendBtn, borderColor: '#c5ccd3', background: WA.scrollBtn }}
              onClick={() => setInput(a.msg)}
              data-testid={`quick-action-${i}`}>
              {a.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Input bar ── */}
      <form onSubmit={handleSend} className="shrink-0 flex items-center gap-2 px-3 py-1.5" style={{ background: WA.inputBarBg }}>

        {/* Input capsule */}
        <div className="flex-1 flex items-center rounded-full px-3" style={{ background: WA.inputBg, border: '1px solid #CCD0D5' }}>
          {/* Emoji icon */}
          <svg className="w-[26px] h-[26px] shrink-0 mr-1 cursor-pointer" viewBox="0 0 24 24" fill={WA.meta}>
            <path d="M9.153 11.603c.795 0 1.44-.88 1.44-1.962s-.645-1.962-1.44-1.962-1.44.88-1.44 1.962.645 1.962 1.44 1.962zm5.694 0c.795 0 1.44-.88 1.44-1.962s-.645-1.962-1.44-1.962-1.44.88-1.44 1.962.645 1.962 1.44 1.962zM12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm0 22C6.486 22 2 17.514 2 12S6.486 2 12 2s10 4.486 10 10-4.486 10-10 10zm5.5-7.5c0 .001-.001.002-.001.002-.552 2.08-2.637 3.498-5.499 3.498s-4.946-1.418-5.5-3.5c-.002 0-.002-.001-.002-.002-.046-.168.079-.357.256-.357h10.49c.178 0 .303.189.256.359z"/>
          </svg>

          <input ref={inputRef} value={input} onChange={(e) => setInput(e.target.value)}
            placeholder="Type a message" disabled={sending}
            className="flex-1 bg-transparent py-[9px] text-[15px] outline-none placeholder:text-[#667781]"
            style={{ color: WA.text }}
            data-testid="chat-input"/>

          {/* Attachment */}
          <svg className="w-[24px] h-[24px] shrink-0 ml-2 cursor-pointer rotate-45" viewBox="0 0 24 24" fill={WA.meta}>
            <path d="M1.816 15.556v.002c0 1.502.584 2.912 1.646 3.972s2.472 1.647 3.974 1.647a5.58 5.58 0 003.972-1.645l9.547-9.548c.769-.768 1.147-1.767 1.058-2.817-.079-.968-.548-1.927-1.319-2.698-1.594-1.592-4.068-1.711-5.517-.262l-7.916 7.915c-.881.881-.792 2.25.214 3.261.959.958 2.423 1.053 3.263.215l5.511-5.512c.28-.28.267-.722.053-.936l-.244-.244c-.191-.191-.567-.349-.957.04l-5.506 5.506c-.18.18-.635.127-.976-.214-.098-.097-.576-.613-.213-.973l7.915-7.917c.818-.817 2.267-.699 3.23.262.5.501.802 1.1.849 1.685.051.573-.156 1.111-.589 1.543l-9.547 9.549a3.97 3.97 0 01-2.829 1.171 3.975 3.975 0 01-2.83-1.171 3.973 3.973 0 01-1.172-2.828c0-1.071.415-2.076 1.172-2.83l7.209-7.211c.157-.157.264-.579.028-.814L11.5 4.36a.572.572 0 00-.834.018l-7.205 7.207a5.577 5.577 0 00-1.645 3.971z"/>
          </svg>
        </div>

        {/* Send / Mic button */}
        {input.trim() ? (
          <button type="submit" disabled={sending}
            className="w-[48px] h-[48px] rounded-full flex items-center justify-center shrink-0 transition-transform active:scale-95"
            style={{ background: WA.sendBtn }}
            data-testid="send-button">
            <svg className="w-5 h-5 ml-[2px]" fill="white" viewBox="0 0 24 24"><path d="M1.101 21.757L23.8 12.028 1.101 2.3l.011 7.912 13.239 1.816-13.239 1.817-.011 7.912z"/></svg>
          </button>
        ) : (
          <button type="button"
            className="w-[48px] h-[48px] rounded-full flex items-center justify-center shrink-0"
            style={{ background: WA.sendBtn }}>
            <svg className="w-5 h-5" fill="white" viewBox="0 0 24 24"><path d="M11.999 14.942c2.001 0 3.531-1.53 3.531-3.531V4.35c0-2.001-1.53-3.531-3.531-3.531S8.469 2.35 8.469 4.35v7.061c0 2.001 1.53 3.531 3.53 3.531zm6.238-3.53c0 3.531-2.942 6.002-6.237 6.002s-6.237-2.471-6.237-6.002H3.761c0 4.001 3.178 7.297 7.061 7.885v3.884h2.354v-3.884c3.884-.588 7.061-3.884 7.061-7.885h-2z"/></svg>
          </button>
        )}
      </form>
    </div>
  );
};

export default ChatPage;
